import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Users, Search, Settings, Coins, HardDrive, RefreshCw, Eye, Plus } from "lucide-react";

interface UserBalance {
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    subscriptionStatus: string;
    createdAt: string;
  };
  balances: {
    illustrationCredits: number;
    extraStorageGb: number;
    hasUnlimitedStorage: boolean;
    lastUpdated: string | null;
  };
  currentUsage: {
    baseIllustrationsUsed: number;
    storageUsedBytes: number;
    storageUsedGb: number;
    periodStart: string | null;
    periodEnd: string | null;
  };
  subscriptionAllowances: {
    baseIllustrations: number;
    baseStorageGb: number;
  };
}

interface AdminAddonBalancesProps {
  onSelectUser?: (userId: number) => void;
  onAdjustBalance?: (userId: number) => void;
}

export default function AdminAddonBalances({ onSelectUser, onAdjustBalance }: AdminAddonBalancesProps) {
  const [search, setSearch] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 25;

  // Fetch all users with balances
  // Construct URL with query parameters
  const buildUsersUrl = () => {
    const params: string[] = [];
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    if (subscriptionFilter) params.push(`subscriptionStatus=${encodeURIComponent(subscriptionFilter)}`);
    params.push(`limit=${pageSize}`);
    params.push(`offset=${currentPage * pageSize}`);
    return `/api/admin/addons/users?${params.join('&')}`;
  };

  const { data: usersResponse, isLoading, error, refetch } = useQuery<{
    success: boolean;
    data: {
      users: UserBalance[];
      pagination: {
        limit: number;
        offset: number;
        hasMore: boolean;
        total: number;
      };
    };
  }>({
    queryKey: [buildUsersUrl()],
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const users = usersResponse?.data?.users || [];
  const pagination = usersResponse?.data?.pagination;

  const getSubscriptionBadge = (status: string) => {
    switch (status) {
      case "trial":
        return <Badge variant="outline" className="text-blue-600">Trial</Badge>;
      case "active":
        return <Badge variant="default" className="bg-green-600">Active</Badge>;
      case "lifetime":
        return <Badge variant="default" className="bg-purple-600">Lifetime</Badge>;
      case "canceled":
        return <Badge variant="destructive">Canceled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getBalanceStatus = (credits: number, used: number, allowance: number) => {
    const totalAvailable = credits + Math.max(0, allowance - used);
    if (totalAvailable > 20) return { variant: "default", label: "Good" };
    if (totalAvailable > 5) return { variant: "secondary", label: "Low" };
    return { variant: "destructive", label: "Critical" };
  };

  const getStorageStatus = (hasUnlimited: boolean, extraGb: number, usedGb: number, allowanceGb: number) => {
    if (hasUnlimited) return { variant: "default", label: "Unlimited" };
    const totalAvailable = extraGb + Math.max(0, allowanceGb - usedGb);
    if (totalAvailable > 5) return { variant: "default", label: "Good" };
    if (totalAvailable > 1) return { variant: "secondary", label: "Low" };
    return { variant: "destructive", label: "Critical" };
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(0); // Reset to first page when searching
  };

  const handleSubscriptionFilterChange = (value: string) => {
    setSubscriptionFilter(value === "all" ? "" : value);
    setCurrentPage(0); // Reset to first page when filtering
  };

  if (isLoading) {
    return (
      <Card data-testid="admin-addon-balances">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Add-on Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="admin-addon-balances">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Add-on Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center text-red-600 py-8">
            <AlertTriangle className="w-5 h-5 mr-2" />
            Error loading user balances: {error instanceof Error ? error.message : "Unknown error"}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="admin-addon-balances">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Add-on Balances
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetch()}
            data-testid="refresh-balances-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex gap-4 mt-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search by email, name..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
              data-testid="search-users-input"
            />
          </div>
          <Select value={subscriptionFilter || "all"} onValueChange={handleSubscriptionFilterChange}>
            <SelectTrigger className="w-48" data-testid="subscription-filter-select">
              <SelectValue placeholder="Filter by subscription" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Subscriptions</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="lifetime">Lifetime</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {users.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            {search || subscriptionFilter ? "No users found matching your criteria." : "No users found."}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{pagination?.total || users.length}</div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">
                    {users.filter(u => u.balances.illustrationCredits > 0).length}
                  </div>
                  <p className="text-sm text-muted-foreground">With Credits</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">
                    {users.filter(u => u.balances.hasUnlimitedStorage).length}
                  </div>
                  <p className="text-sm text-muted-foreground">Unlimited Storage</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">
                    {users.filter(u => u.user.subscriptionStatus === 'lifetime').length}
                  </div>
                  <p className="text-sm text-muted-foreground">Lifetime Users</p>
                </CardContent>
              </Card>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Subscription</th>
                    <th className="text-left p-3">Illustration Credits</th>
                    <th className="text-left p-3">Storage</th>
                    <th className="text-left p-3">Usage</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((userBalance) => {
                    const illustrationStatus = getBalanceStatus(
                      userBalance.balances.illustrationCredits,
                      userBalance.currentUsage.baseIllustrationsUsed,
                      userBalance.subscriptionAllowances.baseIllustrations
                    );
                    
                    const storageStatus = getStorageStatus(
                      userBalance.balances.hasUnlimitedStorage,
                      userBalance.balances.extraStorageGb,
                      userBalance.currentUsage.storageUsedGb,
                      userBalance.subscriptionAllowances.baseStorageGb
                    );

                    return (
                      <tr 
                        key={userBalance.user.id} 
                        className="border-b hover:bg-gray-50"
                        data-testid={`user-row-${userBalance.user.id}`}
                      >
                        <td className="p-3">
                          <div>
                            <div className="font-medium">{userBalance.user.email}</div>
                            {(userBalance.user.firstName || userBalance.user.lastName) && (
                              <div className="text-sm text-muted-foreground">
                                {userBalance.user.firstName} {userBalance.user.lastName}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              ID: {userBalance.user.id}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          {getSubscriptionBadge(userBalance.user.subscriptionStatus)}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Coins className="h-4 w-4 text-purple-600" />
                            <span className="font-medium">{userBalance.balances.illustrationCredits}</span>
                            <Badge variant={illustrationStatus.variant as any}>
                              {illustrationStatus.label}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Used: {userBalance.currentUsage.baseIllustrationsUsed}/{userBalance.subscriptionAllowances.baseIllustrations} base
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <HardDrive className="h-4 w-4 text-teal-600" />
                            {userBalance.balances.hasUnlimitedStorage ? (
                              <span className="font-medium">Unlimited</span>
                            ) : (
                              <span className="font-medium">{userBalance.balances.extraStorageGb}GB</span>
                            )}
                            <Badge variant={storageStatus.variant as any}>
                              {storageStatus.label}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Used: {userBalance.currentUsage.storageUsedGb}GB
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm">
                            <div>Period: {userBalance.currentUsage.periodStart ? 
                              new Date(userBalance.currentUsage.periodStart).toLocaleDateString() : 'N/A'}</div>
                            <div className="text-muted-foreground">
                              Ends: {userBalance.currentUsage.periodEnd ? 
                              new Date(userBalance.currentUsage.periodEnd).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onSelectUser?.(userBalance.user.id)}
                              data-testid={`view-user-${userBalance.user.id}`}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onAdjustBalance?.(userBalance.user.id)}
                              data-testid={`adjust-balance-${userBalance.user.id}`}
                            >
                              <Settings className="h-3 w-3 mr-1" />
                              Adjust
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} users
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    data-testid="previous-page-button"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!pagination.hasMore}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    data-testid="next-page-button"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}